import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

/** Which grid the service-history tab is showing. */
const SERVICE_VIEWS = ['visits', 'callbacks'] as const;

export type ServiceView = (typeof SERVICE_VIEWS)[number];

const DEFAULT_SERVICE_VIEW: ServiceView = 'visits';

const serviceViewParam = 'serviceView';

const isServiceView = (value: string | null): value is ServiceView =>
  value !== null && SERVICE_VIEWS.includes(value as ServiceView);

/** The sub-tab on screen, kept in the URL so a link can point at either grid. */
export function useServiceView(): [ServiceView, (next: ServiceView) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get(serviceViewParam);
  const view = isServiceView(fromUrl) ? fromUrl : DEFAULT_SERVICE_VIEW;

  const setView = useCallback(
    (next: ServiceView) => {
      setSearchParams(
        (prev) => {
          if (next === DEFAULT_SERVICE_VIEW) {
            prev.delete(serviceViewParam);
          } else {
            prev.set(serviceViewParam, next);
          }
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return [view, setView];
}
